// store/consent.ts — P5 GDPR Art.7 personalization consent (FE mirror).
//
// FSM: docs/contracts/aha/state_machines/personalization_consent.yaml.
// BE is canonical; FE mirrors and provides optimistic UI with rollback on
// API failure (ADDENDUM correction 2).
import { create } from 'zustand';
import {
  acceptPersonalizationConsent,
  declinePersonalizationConsent,
  withdrawPersonalizationConsent,
  CURRENT_CONSENT_TEXT_VERSION,
} from '@/lib/api/aha';
import { fireAhaEvent } from '@/lib/analytics';
import type { PersonalizationConsentState } from '@/types/api';

export type ConsentState = PersonalizationConsentState['state'];

interface ConsentStore {
  state: ConsentState;
  acceptedAt: string | null;
  declinedAt: string | null;
  withdrawnAt: string | null;
  consentTextVersion: string | null;
  bannerVisible: boolean;
  showBanner: () => void;
  hideBanner: () => void;
  accept: () => Promise<void>;
  decline: () => Promise<void>;
  withdraw: (reason?: string) => Promise<void>;
}

export const useConsent = create<ConsentStore>((set, get) => ({
  state: 'unset',
  acceptedAt: null,
  declinedAt: null,
  withdrawnAt: null,
  consentTextVersion: null,
  bannerVisible: false,
  showBanner: () => set({ bannerVisible: true }),
  hideBanner: () => set({ bannerVisible: false }),
  accept: async () => {
    const prev = get();
    set({ state: 'asked' });
    try {
      const res = await acceptPersonalizationConsent({
        accept: true,
        consent_text_version: prev.consentTextVersion ?? CURRENT_CONSENT_TEXT_VERSION,
      });
      set({
        state: res.state as ConsentState,
        acceptedAt: res.accepted_at ?? null,
        consentTextVersion: res.consent_text_version,
        bannerVisible: false,
      });
      fireAhaEvent('personalization.consent.accepted', { user_id: 'auth' });
    } catch (err) {
      set({ state: prev.state });
      fireAhaEvent('personalization.consent.error', { user_id: 'auth', stage: 'accept' });
      throw err;
    }
  },
  decline: async () => {
    const prev = get();
    set({ state: 'asked' });
    try {
      const res = await declinePersonalizationConsent();
      set({
        state: res.state as ConsentState,
        declinedAt: res.declined_at ?? null,
        consentTextVersion: res.consent_text_version,
        bannerVisible: false,
      });
      fireAhaEvent('personalization.consent.declined', { user_id: 'auth' });
    } catch (err) {
      set({ state: prev.state });
      fireAhaEvent('personalization.consent.error', { user_id: 'auth', stage: 'decline' });
      throw err;
    }
  },
  withdraw: async (reason?: string) => {
    const prev = get();
    set({ state: 'asked' });
    try {
      const res = await withdrawPersonalizationConsent(reason);
      set({
        state: 'withdrawn',
        withdrawnAt: res.withdrawn_at,
      });
      fireAhaEvent('personalization.consent.withdrawn', { user_id: 'auth' });
    } catch (err) {
      set({ state: prev.state });
      fireAhaEvent('personalization.consent.error', { user_id: 'auth', stage: 'withdraw' });
      throw err;
    }
  },
}));

// hydrateConsentFromMe — sync local store from the GET /me response.
export function hydrateConsentFromMe(consent: PersonalizationConsentState | null | undefined): void {
  if (!consent) {
    useConsent.setState({ state: 'unset', bannerVisible: false });
    return;
  }
  useConsent.setState({
    state: consent.state,
    acceptedAt: consent.accepted_at ?? null,
    declinedAt: consent.declined_at ?? null,
    withdrawnAt: consent.withdrawn_at ?? null,
    consentTextVersion: consent.consent_text_version ?? null,
    // Banner visible if we never asked, or version was bumped, or user
    // declined and re-prompt window elapsed (BE owns the latter; here we
    // simply respect server state).
    bannerVisible: consent.state === 'unset' || consent.state === 'asked',
  });
}
